import React, { useEffect, useState } from 'react';
import { Search, Filter, Clock, User, Activity, RefreshCw, ChevronLeft, ChevronRight, Download, X, Monitor, Globe, Server, Hash, Activity as ActivityIcon, Terminal, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import API from '../api';

const ACTION_COLORS = {
  CREATE: { bg: '#2dce8918', color: '#2dce89' },
  READ: { bg: '#11cdef18', color: '#11cdef' },
  UPDATE: { bg: '#fb634018', color: '#fb6340' },
  DELETE: { bg: '#f5365c18', color: '#f5365c' },
  LOGIN: { bg: '#8965e018', color: '#8965e0' },
};

const ACTION_LABELS = {
  CREATE: 'Tambah',
  READ: 'Lihat',
  UPDATE: 'Ubah',
  DELETE: 'Hapus',
  LOGIN: 'Login',
};

export default function LogSystem() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadLogs = (p = page) => {
    setLoading(true);
    setError('');

    const params = { page: p, limit: 50 };
    if (actionFilter) params.action = actionFilter;
    if (entityFilter) params.entity = entityFilter;
    if (usernameFilter) params.username = usernameFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    API.get('/system-logs', { params })
      .then(res => {
        setLogs(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(p);
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Gagal memuat log');
        setLogs([]);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(1); }, []);

  const handleFilter = () => loadLogs(1);

  const resetFilters = () => {
    setActionFilter('');
    setEntityFilter('');
    setUsernameFilter('');
    setDateFrom('');
    setDateTo('');
    loadLogs(1);
  };

  const exportCSV = async () => {
    try {
      const params = { export: 'csv' };
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity = entityFilter;
      if (usernameFilter) params.username = usernameFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await API.get('/system-logs', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      link.setAttribute('download', `system_logs_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV gagal', err);
      alert('Gagal mengexport CSV. Silahkan coba lagi.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="page log-system-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Log Sistem</h2>
            <p className="page-desc">Catatan aktivitas seluruh pengguna dalam sistem</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {total > 0 && (
              <button className="btn btn-secondary" onClick={exportCSV}>
                <Download size={16} />
                <span>Export CSV</span>
              </button>
            )}
            {total > 0 && (
              <button className="btn btn-secondary" onClick={() => { setShowClearModal(true); setClearResult(null); }} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                <Trash2 size={16} />
                <span>Hapus Semua</span>
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => loadLogs(page)} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="search-box" style={{ minWidth: 180, flex: '0 1 auto' }}>
          <User size={16} />
          <input
            type="text"
            placeholder="Filter username..."
            value={usernameFilter}
            onChange={e => setUsernameFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
        </div>
        <div className="filter-select">
          <Filter size={16} />
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
            <option value="">Semua Aksi</option>
            <option value="CREATE">Tambah</option>
            <option value="READ">Lihat</option>
            <option value="UPDATE">Ubah</option>
            <option value="DELETE">Hapus</option>
            <option value="LOGIN">Login</option>
          </select>
        </div>
        <div className="search-box" style={{ minWidth: 150, flex: '0 1 auto' }}>
          <Activity size={16} />
          <input
            type="text"
            placeholder="Filter entitas..."
            value={entityFilter}
            onChange={e => setEntityFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="filter-date"
            placeholder="Dari tanggal"
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
          />
          <span className="text-muted">s/d</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="filter-date"
            placeholder="Sampai tanggal"
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
          />
        </div>
        <button className="btn btn-primary" onClick={handleFilter} style={{ padding: '8px 16px' }}>
          <Search size={16} />
          <span>Cari</span>
        </button>
        {(actionFilter || entityFilter || usernameFilter || dateFrom || dateTo) && (
          <button className="btn btn-secondary" onClick={resetFilters} style={{ padding: '8px 16px' }}>
            Reset
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="page-loading">Memuat log...</div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Waktu</th>
                  <th style={{ width: 100 }}>User</th>
                  <th style={{ width: 90 }}>Aksi</th>
                  <th style={{ width: 150 }}>Entitas</th>
                  <th>Deskripsi</th>
                  <th style={{ width: 80 }}>Durasi</th>
                  <th style={{ width: 80 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="log-row" onClick={() => setSelectedLog(log)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Clock size={12} className="text-muted" />
                        <span>{formatDate(log.created_at)}</span>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                        {log.username || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="role-badge" style={{
                        background: (ACTION_COLORS[log.action] || ACTION_COLORS.READ).bg,
                        color: (ACTION_COLORS[log.action] || ACTION_COLORS.READ).color,
                        fontSize: '0.72rem',
                      }}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      <code style={{
                        background: 'var(--gray-100)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: '0.78rem',
                        color: 'var(--gray-600)',
                      }}>
                        {log.entity || '-'}
                      </code>
                    </td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{log.description || '-'}</span>
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 2 }}>
                        {log.request_method} {log.request_path}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                      {log.duration_ms > 0 ? `${log.duration_ms}ms` : '-'}
                    </td>
                    <td>
                      <span className={`status-toggle ${log.status_code < 400 ? 'active' : 'inactive'}`}>
                        {log.status_code || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted" style={{ padding: 40 }}>Belum ada log aktivitas</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                <span className="text-muted">Total {total} log — Halaman {page} dari {totalPages}</span>
              </div>
              <div className="pagination-btns">
                <button
                  className="btn btn-secondary"
                  disabled={page <= 1}
                  onClick={() => loadLogs(page - 1)}
                  style={{ padding: '8px 12px' }}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={page >= totalPages}
                  onClick={() => loadLogs(page + 1)}
                  style={{ padding: '8px 12px' }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearModal && (
        <div className="modal-overlay" onClick={() => { if (!clearing) setShowClearModal(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--danger)' }}>Hapus Semua Log</h3>
              {!clearing && (
                <button className="btn-icon" onClick={() => setShowClearModal(false)}>
                  <X size={18} />
                </button>
              )}
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '24px' }}>
              {clearResult ? (
                <>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: '#d1fae5', color: '#065f46',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <CheckCircle size={28} />
                  </div>
                  <h4 style={{ color: '#065f46', marginBottom: 8, fontSize: '1rem' }}>
                    Berhasil!
                  </h4>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                    {clearResult.message}
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: '#fee2e2', color: '#dc2626',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <AlertTriangle size={28} />
                  </div>
                  <h4 style={{ color: 'var(--gray-600)', marginBottom: 8 }}>Konfirmasi</h4>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                    Anda yakin ingin menghapus <strong>semua {total} log</strong> aktivitas?
                    Tindakan ini <strong style={{ color: 'var(--danger)' }}>tidak bisa dibatalkan</strong>.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center', gap: 12 }}>
              {clearResult ? (
                <button className="btn btn-primary" onClick={() => { setShowClearModal(false); setClearResult(null); loadLogs(1); }}>
                  Tutup
                </button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => setShowClearModal(false)} disabled={clearing}>
                    Batal
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      setClearing(true);
                      try {
                        const res = await API.delete('/system-logs');
                        setClearResult(res.data);
                        setLogs([]);
                        setTotal(0);
                        setTotalPages(1);
                      } catch (err) {
                        setClearResult({ message: 'Gagal menghapus log: ' + (err.response?.data?.error || err.message) });
                      } finally {
                        setClearing(false);
                      }
                    }}
                    disabled={clearing}
                    style={{ background: '#dc2626', color: 'white' }}
                  >
                    {clearing ? (
                      <><RefreshCw size={16} className="spinner" /> Menghapus...</>
                    ) : (
                      <><Trash2 size={16} /> Ya, Hapus Semua</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="modal-content modal-detail" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h3>Detail Log #{selectedLog.id}</h3>
              <button className="btn-icon" onClick={() => setSelectedLog(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px 16px', fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>ID Log</span>
                <span>{selectedLog.id}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Waktu</span>
                <span>{formatDate(selectedLog.created_at)}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Username</span>
                <span>{selectedLog.username || <span className="text-muted">-</span>}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>User ID</span>
                <span>{selectedLog.user_id || <span className="text-muted">-</span>}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}><ActivityIcon size={14} style={{ marginRight: 4 }} />Aksi</span>
                <span><span className="role-badge" style={{
                  background: (ACTION_COLORS[selectedLog.action] || ACTION_COLORS.READ).bg,
                  color: (ACTION_COLORS[selectedLog.action] || ACTION_COLORS.READ).color,
                  fontSize: '0.75rem',
                }}>{ACTION_LABELS[selectedLog.action] || selectedLog.action}</span></span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}><Terminal size={14} style={{ marginRight: 4 }} />Entitas</span>
                <span><code style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem' }}>{selectedLog.entity || '-'}</code></span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}><Hash size={14} style={{ marginRight: 4 }} />Entity ID</span>
                <span>{selectedLog.entity_id || <span className="text-muted">-</span>}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Deskripsi</span>
                <span>{selectedLog.description || '-'}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600, borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}><Server size={14} style={{ marginRight: 4 }} />Method</span>
                <span style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}><code style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 4 }}>{selectedLog.request_method}</code></span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Path</span>
                <span style={{ wordBreak: 'break-all' }}><code style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem' }}>{selectedLog.request_path}</code></span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Status</span>
                <span><span className={`status-toggle ${selectedLog.status_code < 400 ? 'active' : 'inactive'}`}>{selectedLog.status_code}</span></span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>Durasi</span>
                <span>{selectedLog.duration_ms > 0 ? `${selectedLog.duration_ms} ms` : <span className="text-muted">-</span>}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600, borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}><Globe size={14} style={{ marginRight: 4 }} />IP Address</span>
                <span style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10, fontFamily: 'monospace', fontSize: '0.8rem' }}>{selectedLog.ip_address || <span className="text-muted">-</span>}</span>

                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}><Monitor size={14} style={{ marginRight: 4 }} />User Agent</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', wordBreak: 'break-word', lineHeight: 1.4 }}>{selectedLog.user_agent || <span className="text-muted">-</span>}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
